import PluginAddToCart from '../../../components/plugins/PluginAddToCart'
import PluginsImagePreview from '../../../components/plugins/PluginsImagePreview'
import AppLayout from '../../../components/layouts/AppLayout.js'
import PluginAbout from '../../../components/plugins/PluginAbout'
import PluginRating from '../../../components/plugins/PluginRating'
import PluginComment from '../../../components/plugins/PluginComment'
import PluginGuestMessage from '../../../components/plugins/PluginGuestMessage'
import PluginAboutManufacturer from '../../../components/plugins/PluginAboutManufacturer'
import { useRouter } from 'next/router'
import { GET_PRODUCT, GET_PRODUCT_BY_SLUG } from '../../../services/graphql/queries/getProduct'
import { withToken } from '../../../services/client'
import { GET_RATING_STATS, GET_RATING_STATS_BY_SLUG } from '../../../services/graphql/queries/getRatingStats'
import { useEffect, useMemo, useState } from 'react'
import { useLazyQuery } from '@apollo/client'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { selectMe } from '../../../features/auth/authReducer'
import { useSelector } from 'react-redux'
import PluginRelated from '../../../components/plugins/PluginRelated'
import Head from 'next/head'
import { useTranslation } from 'next-i18next'
import { GET_PRODUCT_RATINGS } from '../../../services/graphql/queries/getProductRatings'
import Error from 'next/error'
import SellerSupport from '../../../components/seller/SellerSupport'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import remarkBreaks from 'remark-breaks'

export default function Plugin ({ plugin, ratingStats, comments, language, error }) {
  // As plugin has been fetched beforehand, this HAS to be available here, if we found it
  if (!plugin?.id) {
    // Generic error page from Next.js
    return <Error statusCode={404} />
  }

  const router = useRouter()
  const { t } = useTranslation('plugins')
  const me = useSelector(state => selectMe(state.auth))
  const [pluginState, setPluginState] = useState(plugin)
  const [ratingStatsState, setRatingStatsState] = useState(ratingStats)
  const [commentsState, setCommentsState] = useState(comments)
  const currentDescription = useMemo(() => {
    const description = plugin?.descriptions?.find(description => description.language === language)

    if (description && (description.intro || description.description)) {
      return description
    }

    return plugin?.descriptions[0]
  })
  const currentFeatures = useMemo(() => {
    const features = plugin?.features?.filter(feature => (
      feature.language === language && (feature.name || feature.description)
    ))

    if (features.length > 0) {
      return features
    }

    // EN is the default language
    return plugin?.features.filter(feature => feature.language === 'EN')
  })
  const [fetchComments] = useLazyQuery(GET_PRODUCT_RATINGS, {
    fetchPolicy: 'no-cache',
    variables: {
      page: 1,
      first: 3, // We limit the number of comments to 3. We do not need more in the detail page.
      orderBy: [{ column: 'CREATED_AT', order: 'DESC' }],
      where: {
        HAS: {
          relation: 'product',
          condition: { column: 'SLUG', operator: 'EQ', value: router.query.slug }
        }
      }
    },
    onCompleted: data => {
      setCommentsState(data.productRatings.data)
    }
  })
  const [fetchRatingStats] = useLazyQuery(GET_RATING_STATS, {
    fetchPolicy: 'no-cache',
    variables: {
      id: plugin.id
    },
    onCompleted: data => {
      setRatingStatsState(data.ratingStats)
    }
  })
  const [fetchPlugin] = useLazyQuery(GET_PRODUCT, {
    fetchPolicy: 'no-cache',
    variables: {
      id: plugin.id,
      first: 3,
      language
    },
    onCompleted: data => {
      setPluginState(data.product)
    }
  })

  const submitTrackerEvent = () => {
    if (!plugin) {
      return
    }

    window.dataLayer = window.dataLayer || []
    window.dataLayer.push(
      {
        event: 'view_item',
        ecommerce: {
          currency: 'EUR',
          value: plugin.starting_price / 100,
          items: [
            {
              item_id: plugin.id,
              item_name: plugin.name,
              index: 0,
              price: plugin.starting_price / 100,
              quantity: 1,
              coupon: '-',
              discount: 0
            }
          ]
        }
      }
    )
  }

  const handleReviewCreated = () => {
    fetchRatingStats()
    fetchPlugin()
    fetchComments()
  }

  useEffect(() => {
    setPluginState(plugin)
    submitTrackerEvent()
  }, [plugin])

  useEffect(() => {
    setRatingStatsState(ratingStats)
  }, [ratingStats])

  useEffect(() => {
    setCommentsState(comments)
  }, [comments])

  return (
    <AppLayout>
      <Head>
        {
          error
            ? <title>{t('detail.meta.title', { name: null })}</title>
            : <title>{t('detail.meta.title', { name: plugin.name })}</title>
        }
        {
          (plugin.descriptions.length && currentDescription) && (
            <meta
              property="og:description"
              content={currentDescription.intro}
            />
          )
        }
        <meta
          property="og:image"
          content="https://cadsoma.com/images/logo.svg"
        />
      </Head>
      {
        (plugin?.company.subscription_type !== 'NONE' && plugin?.company.company_constraints.has_lead === true) && (
          <SellerSupport company={plugin?.company}/>
        )
      }
      {
        error
          ? <div className="container py-5">
            <div className="text-center">
              <div className="p-4 p-md-5 bg-light row align-items-center rounded-3">
                <strong className="pb-3">{t('detail.not_approved.title')}</strong>
                <p className="m-0">{t('detail.not_approved.info')}</p>
              </div>
            </div>
          </div>
          : <div>
            <PluginAddToCart
              plugin={pluginState}
              language={language}
            />
            <div className="appendix appendix--bottomright appendix--turquoise appendix--big">
              <div className="container py-5">
                <div className="d-flex flex-wrap gap-3">
                  {
                    currentFeatures.map(feature => (
                      <div
                        key={feature.id}
                        className="flex-grow-1 flex-shrink-1 flex-basis"
                      >
                        <h3 className="fs-4 text-dark">{feature.name}</h3>
                        {
                          (
                            <ReactMarkdown
                              remarkPlugins={[remarkGfm, remarkBreaks]}
                              linkTarget="_blank"
                            >
                              {feature.description}
                            </ReactMarkdown>
                          )
                        }
                      </div>
                    ))
                  }
                </div>
                <div className="row justify-content-between mt-4 mt-md-5">
                  <PluginAbout
                    plugin={pluginState}
                    description={currentDescription}
                  />
                </div>
              </div>
              {
                (pluginState.medias.length > 0 || pluginState.video_link) && (
                  <div className="bg-light-blue py-5">
                    <div className="container">
                      <h3 className="text-dark mb-3 fw-bold">
                        {t('detail.screenshots')}
                        <span className="ms-1">
                          ({pluginState.video_link ? pluginState.medias.length + 1 : pluginState.medias.length})
                        </span>
                      </h3>
                      <PluginsImagePreview
                        medias={pluginState.medias}
                        videoLink={pluginState.video_link}
                      />
                    </div>
                  </div>
                )
              }
              <div className="bg-light py-5">
                <div className="container">
                  <div className="row justify-content-between align-items-start">
                    <div className="col-12 col-md-6 col-lg-5 col-xl-4">
                      <PluginRating
                        onReviewCreated={() => handleReviewCreated()}
                        plugin={pluginState}
                        ratingStats={ratingStatsState}
                      />
                    </div>
                    { commentsState && (
                      <div className="col-12 col-md-6 col-lg-7">
                        { commentsState.map(rating => (
                          <PluginComment
                            key={rating.id}
                            rating={rating}
                          />
                        ))}
                        {
                          commentsState.length === 0 && (
                            <div>
                              <p>{t('detail.rating.no_reviews')}</p>
                            </div>
                          )
                        }
                        {
                          commentsState.length > 0 && (
                            <button
                              className="btn btn-outline-primary mt-5"
                              onClick={() => router.push('/plugins/' + pluginState.slug + '/reviews')}
                            >{t('detail.rating.show_all_reviews')}</button>
                          )
                        }
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="bg-light">
                <div className="container">
                  {
                    (!me || !me.id) && (
                      <PluginGuestMessage />
                    )
                  }
                </div>
              </div>
              <div className="container">
                <div className="py-5">
                  <PluginAboutManufacturer plugin={pluginState} />
                </div>
                <div className="py-5">
                  <PluginRelated plugin={pluginState} />
                </div>
              </div>
            </div>
          </div>
      }

    </AppLayout>
  )
}

export async function getServerSideProps ({ query, locale, req }) {
  const client = withToken(req.cookies.token, locale)

  const { slug } = query
  const language = locale.toUpperCase()
  let error = false
  let plugin = []
  let comments = []
  let ratingStats = []

  try {
    plugin = await client.query({
      fetchPolicy: 'no-cache',
      query: GET_PRODUCT_BY_SLUG,
      variables: {
        slug
      }
    })

    comments = await client.query({
      fetchPolicy: 'no-cache',
      query: GET_PRODUCT_RATINGS,
      variables: {
        page: 1,
        first: 3, // We limit the number of comments to 3. We do not need more in the detail page.
        orderBy: [{ column: 'CREATED_AT', order: 'DESC' }],
        where: {
          HAS: {
            relation: 'product',
            condition: { column: 'SLUG', operator: 'EQ', value: slug }
          }
        }
      }
    })

    ratingStats = await client.query({
      fetchPolicy: 'no-cache',
      query: GET_RATING_STATS_BY_SLUG,
      variables: {
        slug
      }
    })

    plugin = plugin.data.productBySlug
    comments = comments.data.productRatings.data
    ratingStats = ratingStats.data.ratingStatsBySlug
  } catch (e) {
    error = true
  }

  return {
    props: {
      error,
      language,
      plugin,
      ratingStats,
      comments,
      ...(await serverSideTranslations(locale, ['plugins', 'seller', 'footer', 'header', 'languages', 'categories', 'applications', 'common', 'routes']))
    }
  }
}
